import type {ErrorData} from "@/models";

interface APIError {
  message: string
  code?: string
  errorcode?: string
  __data?: ErrorData
}

export type UrlParameters   = {[key:string]:string}
export type QueryParameters = {[key:string]:string|number|boolean|string[]|number[]|boolean[]|undefined}

interface APIRequestOptions<T> {
  body?: T
  urlParams?: UrlParameters
  queryParams?: QueryParameters
  headers?: Record<string, string>
  auth?: boolean
  timeoutMs?: number
}

export type FormFieldValue = string | Blob | File | undefined

interface APIRawRequestOptions {
  rawBody: BodyInit
  contentType: string
  urlParams?: UrlParameters
  queryParams?: QueryParameters
  headers?: Record<string, string>
  auth?: boolean
}

interface APIFormRequestOptions {
  fields: { [key: string]: FormFieldValue }
  urlParams?: UrlParameters
  queryParams?: QueryParameters
  headers?: Record<string, string>
  auth?: boolean
  timeoutMs?: number
}

export class APIRequestError extends Error {
  public readonly errorcode: string | undefined;

  constructor(message: string, errorcode?: string) {
    super(message);
    this.errorcode = errorcode;
  }
}

export class APIService {
  private TOKEN_KEY = `bolzai_${import.meta.env.VITE_NAMESPACE}_token`
  private onUnauthorizedCallback: (() => void) | null = null

  public static inst = new APIService();

  public setOnUnauthorized(callback: () => void): void {
    this.onUnauthorizedCallback = callback
  }

  // Lazy import to break the cyclic dependency between api.ts and tokenRefresh.ts.
  private async maybeRefreshToken(route: string): Promise<void> {
    if (route === '/auth/refresh') return
    const { refreshTokenIfNeeded } = await import('@/services/tokenRefresh')
    await refreshTokenIfNeeded()
  }

  public async apiFetch<TRequest, TResponse>(method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE', route: string, options: APIRequestOptions<TRequest>): Promise<TResponse> {
    const url = this.buildUrl(route, options.urlParams, options.queryParams);

    const requestHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options.headers
    };

    if (options.auth) {
      await this.maybeRefreshToken(route);
      const token = this.getToken()
      if (token !== null && token.length > 0) {
        requestHeaders.Authorization = `Bearer ${token}`;
      }
    }

    const requestOptions: RequestInit = {
      method,
      headers: requestHeaders,
      signal: this.timeoutSignal(options.timeoutMs)
    }

    if (method !== 'GET') {
      requestOptions.body = '{}';
    }

    if (options.body && method !== 'GET') {
      requestOptions.body = JSON.stringify(options.body)
    }

    return this.executeRequest<TResponse>(url, requestOptions);
  }

  // Sends the body verbatim under a caller-supplied Content-Type — for endpoints
  // that take a raw payload instead of JSON (e.g. text/csv). Goes through
  // executeRequest, so APIError parsing, errorcode and 401-handling behave like apiFetch.
  public async apiFetchRaw<TResponse>(method: 'POST' | 'PUT' | 'PATCH', route: string, options: APIRawRequestOptions): Promise<TResponse> {
    const url = this.buildUrl(route, options.urlParams, options.queryParams);

    const requestHeaders: Record<string, string> = {
      'Content-Type': options.contentType,
      ...options.headers
    };

    if (options.auth) {
      await this.maybeRefreshToken(route);
      const token = this.getToken()
      if (token !== null && token.length > 0) {
        requestHeaders.Authorization = `Bearer ${token}`;
      }
    }

    return this.executeRequest<TResponse>(url, { method, headers: requestHeaders, body: options.rawBody });
  }

  public async apiFetchForm<TResponse>(method: 'POST' | 'PUT' | 'PATCH', route: string, options: APIFormRequestOptions): Promise<TResponse> {
    const url = this.buildUrl(route, options.urlParams, options.queryParams);

    // NOTE: do not set Content-Type — the browser sets the multipart boundary automatically.
    const requestHeaders: Record<string, string> = { ...options.headers };

    if (options.auth) {
      await this.maybeRefreshToken(route);
      const token = this.getToken()
      if (token !== null) {
        requestHeaders.Authorization = `Bearer ${token}`;
      }
    }

    const form = new FormData();
    for (const key of Object.keys(options.fields)) {
      const value = options.fields[key];
      if (value === undefined) continue;
      form.append(key, value);
    }

    return this.executeRequest<TResponse>(url, { method, headers: requestHeaders, body: form, signal: this.timeoutSignal(options.timeoutMs) });
  }

  // fetch itself never times out, so a request only gets a deadline when the caller asks for one.
  private timeoutSignal(timeoutMs?: number): AbortSignal | undefined {
    if (timeoutMs === undefined) return undefined
    return AbortSignal.timeout(timeoutMs)
  }

  private async executeRequest<TResponse>(url: string, requestOptions: RequestInit): Promise<TResponse> {
    try {
      const response = await fetch(url, requestOptions)

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`
        let errorcode: string | undefined;

        try {
          const errorData: APIError = await response.json()
          // Extract error code
          errorcode = errorData.errorcode ?? errorData.__data?.type;
          // Try to get the most specific error message
          if (errorData.__data?.original?.message) {
            errorMessage = errorData.__data.original.message
          } else if (errorData.__data?.message) {
            errorMessage = errorData.__data.message
          } else {
            errorMessage = errorData.message || errorMessage
          }
        } catch {
          // If error response is not JSON, use the default message
        }

        // Handle unauthorized errors - only logout on 401 status
        if (response.status === 401) {
          this.removeToken()
          if (this.onUnauthorizedCallback) {
            this.onUnauthorizedCallback()
          }
        }
        throw new APIRequestError(errorMessage, errorcode)
      }

      if (response.status === 204) {
        return {} as TResponse
      }

      return await response.json()
    } catch (error) {
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Ein unbekannter Fehler ist aufgetreten')
    }
  }

  public buildUrl(url: string, urlparams?: UrlParameters, query?: QueryParameters): string {
    let result = url;

    // Step 1: replace url parameters aka /users/:userid/clients

    for (const key of Object.keys(urlparams ?? {})) {

      const rex = new RegExp(":"+this.escapeRegExp(key)+"(?=(/|$))");

      const r2 = result.replace(rex, urlparams![key]);

      if (result === r2) throw new Error(`Missing url-field "${key}" in url "${url}"`);

      result = r2;
    }

    // Step 2: append query parameter

    const qpkeys = Object.keys(query ?? {}).filter(k => query![k] !== undefined);

    if (qpkeys.length > 0) {
      result += "?";
      result += qpkeys.map(key => {

        const value = query![key];

        if (typeof value === 'string')  return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
        if (typeof value === 'number')  return `${encodeURIComponent(key)}=${value}`;
        if (typeof value === 'boolean') return `${encodeURIComponent(key)}=${value?'true':'false'}`;

        if (Array.isArray(value) && value.findIndex(p => typeof p !== 'string')==-1) {
          return (value as string[])
            .map(p => `${encodeURIComponent(key)}=${encodeURIComponent(p)}`)
            .join('&');
        }

        if (Array.isArray(value) && value.findIndex(p => typeof p !== 'number')==-1) {
          return (value as number[])
            .map(p => `${encodeURIComponent(key)}=${p}`)
            .join('&');
        }

        if (Array.isArray(value) && value.findIndex(p => typeof p !== 'boolean')==-1) return `${encodeURIComponent(key)}=${value.map(() => value?'true':'false').join(",")}`;

        throw new Error(`Invlaid type '${typeof value}' for query-parameter`);

      }).join("&");
    }

    // Step 3: prepend api-url base

    if (result.startsWith('/')) result = import.meta.env.VITE_API_URL + result; else result = import.meta.env.VITE_API_URL + "/" + result;

    // Step 4: return

    return result;
  }

  public escapeRegExp(str: string) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
  }

  public setToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token)
  }

  public getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  public removeToken(): void {
    localStorage.removeItem(this.TOKEN_KEY)
  }

  public isAuthenticated(): boolean {
    const token = this.getToken()
    if (!token) return false

    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      const isExpired = payload.exp * 1000 < Date.now()
      return !isExpired
    } catch {
      return false
    }
  }

  public getTokenExpirySeconds(): number | null {
    const token = this.getToken()
    if (token === null) return null

    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      if (typeof payload.exp !== 'number') return null
      return payload.exp - Math.floor(Date.now() / 1000)
    } catch {
      return null
    }
  }
}

/**
 * Helper function to convert an array or single value to a comma-separated string.
 * Handles both single values and arrays, filtering out empty values.
 *
 * @param value - Single value or array of values
 * @returns Comma-separated string or undefined if empty
 */
export function toCommaSeparated<T extends string>(value: T | T[] | undefined): string | undefined {
  if (value === undefined) {
    return undefined;
  }
  if (Array.isArray(value)) {
    const filtered = value.filter(v => v !== undefined && v !== null && v !== '');
    return filtered.length > 0 ? filtered.join(',') : undefined;
  }
  return value;
}
